import { Capacitor } from "@capacitor/core";
import { ShieldAlert } from "lucide-react";

import { useScreen0Language } from "@/features/language";
import { getTranslations } from "@/i18n";
import {
  canRequestUsagePermission,
  useUsageAccess,
} from "@/lib/native/usageAccess";

import { OnboardingIllustration } from "../OnboardingIllustration";
import { OnboardingVibesOverlay } from "../OnboardingVibesOverlay";
import { getOnboardingIllustrationDimensions } from "../onboardingImagePreload";
import { onboardingImageCandidates } from "../onboardingImageUrl";
import { useOptionalOnboardingAnalytics } from "@/lib/onboarding/OnboardingAnalyticsContext";

type SetupSlideProps = {
  onAdvance: () => void;
};

export function SetupSlide({ onAdvance: _onAdvance }: SetupSlideProps) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const { permission, permissionReady, requiresEntitlement } = useUsageAccess();
  const isNative = Capacitor.isNativePlatform();
  const canRequest = canRequestUsagePermission(
    permissionReady,
    permission,
    requiresEntitlement,
  );
  const setupDimensions = getOnboardingIllustrationDimensions(10);

  return (
    <div className="flex flex-1 flex-col items-center gap-6 pb-4">
      <div className="relative mx-auto w-fit max-w-[280px]">
        <OnboardingVibesOverlay variant="setup" />
        <OnboardingIllustration
          candidates={onboardingImageCandidates(10)}
          alt={t.onboarding.setupImageAlt}
          intrinsicWidth={setupDimensions?.width}
          intrinsicHeight={setupDimensions?.height}
        />
      </div>

      <div className="flex w-full max-w-md flex-col gap-4 text-center">
        <h2 className="text-xl font-semibold leading-snug text-[oklch(92%_0.02_150)]">
          {t.onboarding.setupTitle}
        </h2>
        <p className="text-base leading-7 text-[oklch(82%_0.025_155)]">
          {t.onboarding.setupBody}
        </p>
      </div>

      {isNative && requiresEntitlement ? (
        <section className="liquid-glass-alert-warning flex w-full max-w-md gap-3 p-4 text-[oklch(82%_0.055_85)]">
          <ShieldAlert size={20} className="shrink-0" />
          <p className="text-sm leading-6">{t.permission.entitlementMissing}</p>
        </section>
      ) : null}

      {isNative && canRequest ? (
        <p className="text-center text-sm text-[oklch(68%_0.025_155)]">
          {t.onboarding.setupNativeHint}
        </p>
      ) : null}

      {!isNative ? (
        <p className="text-center text-sm text-[oklch(68%_0.025_155)]">
          {t.onboarding.setupWebHint}
        </p>
      ) : null}
    </div>
  );
}

export function useSetupSlideAction(onAdvance: () => void, options?: { step?: number }) {
  const analyticsStep = options?.step ?? 6;
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const { permission, permissionReady, requiresEntitlement, requestPermission } =
    useUsageAccess();
  const isNative = Capacitor.isNativePlatform();
  const isAndroid = Capacitor.getPlatform() === "android";
  const analytics = useOptionalOnboardingAnalytics();
  const canRequest = canRequestUsagePermission(
    permissionReady,
    permission,
    requiresEntitlement,
  );

  async function handleSetupCta() {
    analytics?.trackCtaClick(analyticsStep, "primary");

    if (!isNative) {
      analytics?.trackPermissionOutcome(analyticsStep, "screen_time", "skipped_web");
      onAdvance();
      return;
    }

    if (canRequest) {
      try {
        const result = await requestPermission();
        analytics?.trackPermissionOutcome(analyticsStep, "screen_time", result.status);
      } catch {
        analytics?.trackPermissionOutcome(analyticsStep, "screen_time", "error");
        // Permission denial still allows completing onboarding.
      }
    } else {
      analytics?.trackPermissionOutcome(analyticsStep, "screen_time", permission?.status ?? "unknown");
    }

    onAdvance();
  }

  const ctaLabel = !isNative
    ? t.onboarding.continue
    : canRequest
      ? isAndroid
        ? t.permission.openUsageAccess
        : t.permission.requestScreenTimeAccess
      : t.onboarding.continue;

  return { handleSetupCta, ctaLabel, setupBusy: false };
}
